/**
 * Shared folder security utilities — single source of truth.
 * Used by all folder row/card components to determine shared state.
 */
import { ROOT_TEAM_ID, type GroupEditCapability } from '@scio3/shared-types';

/** Folder-security shape (v3 additive model). Legacy fields kept readable for the dual-read window. */
type FolderSecurityShape = {
  /** The folder's OWNING team. */
  teamId?: string | null;
  securities?: Array<{ userId: string }> | null;
  /** v3 — team grants ([{teamId, isEdit}]); teamId '-2' = Everyone in the account. */
  securityFolderTeams?: Array<{ teamId: string }> | null;
  /** v3 — security-group grants ([{groupId, isEdit}]). */
  securityGroupIds?: Array<{ groupId: string }> | null;
};

/**
 * Returns true if the folder has non-default security — any real (non-sentinel-`0`) user grant,
 * any team grant (`securityFolderTeams`, in practice the `'-2'` sentinel), or any security-group grant.
 * Sentinel userId `'0'` means "everyone on this team" (the default), NOT a real user.
 */
export function isFolderShared(
  folder: FolderSecurityShape | null | undefined,
  folderLevelSecurity: boolean,
): boolean {
  if (!folder || !folderLevelSecurity) return false;
  const hasRealUser = (folder.securities ?? []).some((s) => s.userId !== '0');
  const hasFolderTeam = folder.securityFolderTeams != null && folder.securityFolderTeams.length > 0;
  const hasGroup = folder.securityGroupIds != null && folder.securityGroupIds.length > 0;
  return hasRealUser || hasFolderTeam || hasGroup;
}

/** Sentinel team id (in securityFolderTeams) meaning "Everyone in the account". */
export const EVERYONE_ACCOUNT_TEAM_ID = '-2';

/**
 * Group Security §4 — "the role caps the grant". Does the group `groupId` have the ROLE-level
 * capability to edit inside the teamspace that owns the folder?
 *
 * Effective edit on a folder via a group = `isEdit && isGroupEditCapable(...)`. The folder-side
 * `securityGroupIds[].isEdit` flag is only HALF the decision: a Viewer-role group carrying
 * `isEdit:true` must resolve to view-only, and an Editor-role group carrying `isEdit:false` must too.
 * Applied per GROUP — the additive OR-merge across a user's groups (v4) happens AFTER the cap, so an
 * Editor-capable group still wins over a Viewer-capable one on the same folder.
 *
 * FAIL CLOSED. `caps` is the backend-computed principal cap (`me.groupCapabilities`); when it is
 * missing/empty or carries no entry for this group, the role is UNKNOWN and the answer is `false`
 * (view-only). A cap that failed open would be a security bug — never default to `true` to make a
 * surface behave.
 *
 * Teamspace precedence — the EXACT mirror of the backend `groupGrantAllowsEdit`, in this order:
 *   1. the folder's teamspace is in `editTeamIds`      ⇒ true
 *   2. the folder's teamspace is in `viewOnlyTeamIds`  ⇒ false (a role held elsewhere never widens it)
 *   3. otherwise                                       ⇒ `editCapableDefault`
 * A null/undefined folder `teamId` IS the ROOT teamspace ('1') — a root folder's teamId is stored
 * either way, so both must resolve to the same bucket (matching `_getTeamIdSelector` on the backend).
 *
 * What `editCapableDefault` MEANS (#2173, 2026-09-22): it is the group's ACCOUNT-level grant verdict
 * (`companies.groups[]`, a statement about every teamspace) when one exists, and `true` when the group
 * has no account-level grant — in a teamspace the group holds NO statement about, THE FOLDER GRANT
 * DECIDES (the owner shared it at Edit deliberately; cross-teamspace sharing must not require adding
 * the group to that teamspace). Only a statement narrows: a team-scoped Read Only ref
 * (`viewOnlyTeamIds`, checked first) or an account-level Viewer (folded into the default). Likewise a
 * group with NO entry in a delivered cap list holds no statement anywhere ⇒ capable; only a MISSING
 * list (the backend never shipped `me.groupCapabilities`) fails closed. Do not re-derive this on the
 * FE — the backend computes it (`buildGroupEditCapability`) and ships it; this function only applies
 * the precedence, so the two implementations of the rule cannot disagree.
 */
export function isGroupEditCapable(
  groupId: string | null | undefined,
  folderTeamId: string | null | undefined,
  caps: readonly GroupEditCapability[] | null | undefined,
): boolean {
  if (groupId == null || caps == null) return false; // no cap LIST ⇒ unresolved principal ⇒ view only
  const cap = caps.find((c) => c != null && c.groupId === groupId);
  if (cap == null) return true; // no entry ⇒ the group holds no role statement anywhere ⇒ the grant decides
  const teamId = folderTeamId == null ? ROOT_TEAM_ID : folderTeamId;
  if ((cap.editTeamIds ?? []).includes(teamId)) return true;
  if ((cap.viewOnlyTeamIds ?? []).includes(teamId)) return false;
  return cap.editCapableDefault === true;
}

/**
 * v3 cross-team VIEW authorization: may the user view this cross-team folder?
 *
 * PRIMARY signal — `folderTeamId` (the `?teamId=` we navigated to) matches the folder's own team.
 * The backend `groups` read filter (open-api group.resolver + `buildFolderAccessClauses`) is
 * deny-by-default: it only RETURNS a folder from another team when that folder is shared to the
 * requester (their team, account-wide '-2', or a group they're in). So a returned folder whose
 * `teamId` equals the requested cross-team IS already authorized by the server. We trust this FIRST
 * because the folder's own security arrays (`securityFolderTeams`/`securities`/`securityGroupIds`)
 * are frequently `null` on the client — Apollo field-merge races between the folder-list and
 * folder-detail `groups` queries drop them — which made re-deriving from those arrays deny on the
 * 2nd visit even though the 1st worked (#GS-crossteam-reentry).
 *
 * FALLBACK — when `folderTeamId` is not supplied or doesn't match, re-derive from whatever security
 * surfaces are present: `securityFolderTeams` (active team or '-2'), or the presence of any group
 * grant (the server only returns a group-restricted folder to its members).
 *
 * Callers still gate on `currentGroup !== null` first — a null folder means the server denied access.
 */
export function isCrossTeamViewAuthorized(
  folder: FolderSecurityShape | null | undefined,
  activeTeamId: string,
  folderTeamId?: string | null,
): boolean {
  if (!folder) return false;
  // Primary: the server returned this exact cross-team folder ⇒ it is shared to us ⇒ authorized.
  if (folderTeamId && folder.teamId === folderTeamId) return true;
  // Fallback: re-derive from the security surfaces (may be nulled in cache — hence not primary).
  const v3Teams = folder.securityFolderTeams ?? [];
  if (v3Teams.some((t) => t?.teamId === activeTeamId || t?.teamId === EVERYONE_ACCOUNT_TEAM_ID)) {
    return true;
  }
  return (folder.securityGroupIds ?? []).length > 0;
}

/**
 * Stable serialization of the authorization-relevant fields, for the real-time revocation-detection
 * diff (was-authorized → now-not). MUST include every surface `isCrossTeamViewAuthorized` reads so a
 * grant removal is observed. Returns the literal `'null'` for a missing folder (the "no data yet"
 * sentinel the revocation effects branch on). v4: the legacy `securityTeams` surface is gone.
 */
export function folderAuthKey(folder: FolderSecurityShape | null | undefined): string {
  if (!folder) return 'null';
  return JSON.stringify({
    tid: folder.teamId ?? null,
    ft: folder.securityFolderTeams ?? null,
    gt: folder.securityGroupIds ?? null,
    // `securities` is part of the key because v4 M1 keys teamspace visibility on THIS array alone
    // (Rule 3: empty ⇒ the owning teamspace sees + edits the folder). Omitting it meant a change that
    // flips access — adding the `'-1'` Only-Admin sentinel, or restricting to named users — produced
    // an IDENTICAL key, so revocation went undetected and any row memoized on this key stayed stale.
    sec: folder.securities ?? null,
  });
}

/** Was the folder captured by `folderAuthKey` view-authorized for `activeTeamId`? Safe on malformed input. */
export function isAuthorizedFromFolderAuthKey(
  key: string,
  activeTeamId: string,
  folderTeamId?: string | null,
): boolean {
  if (key === 'null') return false;
  try {
    const p = JSON.parse(key) as {
      tid?: string | null;
      ft?: Array<{ teamId: string }> | null;
      gt?: Array<{ groupId: string }> | null;
    } | null;
    if (!p) return false;
    return isCrossTeamViewAuthorized(
      { teamId: p.tid, securityFolderTeams: p.ft, securityGroupIds: p.gt },
      activeTeamId,
      folderTeamId,
    );
  } catch {
    return false;
  }
}
